/**
 * Operadic-side diagrams: the operadic tree, an animated substitution, the
 * safety-closure tree (safe-in → safe-out), and the free-operad program
 * term used in the Operad explainer's Formal tier.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, SAFE } from "../../../design-system/tokens.js";
import { usePulsePhase } from "../../shared/index.js";
import {
  ANIMATION_PERIOD_MS,
  ARITY3_HUB,
  ARITY3_LEAF_1,
  ARITY3_LEAF_2,
  ARITY3_LEAF_3,
  ARITY3_LEAVES,
  ARITY3_ROOT,
  HubNode,
  Leaf,
  OperadicEdges,
  RootNode,
} from "./primitives.js";

/**
 * Operadic composition tree (arity 3). Three labelled inputs at the top fan
 * into a single output at the bottom. The headline picture for the operad
 * card.
 */
export function OperadicTree({
  accent,
  title = "Operad operation f ∈ P(3)",
}: {
  readonly accent: string;
  readonly title?: string;
}): React.JSX.Element {
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        {title}
      </figcaption>
      <svg
        viewBox="0 0 192 160"
        role="img"
        aria-label="Operadic tree: three inputs fan into one output"
      >
        <OperadicEdges accent={accent} />
        {ARITY3_LEAVES.map((leaf) => (
          <Leaf key={leaf.id} spec={leaf} accent={accent} />
        ))}
        <HubNode cx={ARITY3_HUB.x} cy={ARITY3_HUB.y} label="f" accent={accent} />
        <RootNode cx={ARITY3_ROOT.x} cy={ARITY3_ROOT.y} label={ARITY3_ROOT.label} accent={accent} />
        <text x={4} y={14} fontSize="9" fontFamily={FONT.mono} fill={GRAY[600]}>
          inputs
        </text>
        <text x={4} y={150} fontSize="9" fontFamily={FONT.mono} fill={GRAY[600]}>
          output
        </text>
      </svg>
    </figure>
  );
}

/**
 * Operadic substitution animation: a child tree slides into a parent's leaf
 * slot and the composed tree settles as one operation of higher arity.
 *
 * This is the action that makes "if every input is safe, the composite is
 * safe" structural rather than a runtime check.
 */
export function OperadicSubstitutionAnimation({
  accent,
}: {
  readonly accent: string;
}): React.JSX.Element {
  const phase = usePulsePhase(4, ANIMATION_PERIOD_MS);
  // Phase 0: parent has an open slot 2; child operation g floats above.
  // Phase 1: g slides toward the open slot.
  // Phase 2: g is seated in the slot; the composite has arity 4.
  // Phase 3: hold; safety propagates and the root reads as one operation.
  const showChildAttached = phase >= 2;
  // Phase 0/3: lifted; phase 1: descending; phase 2: docked into slot.
  const childTranslateY = phase === 0 ? -36 : phase === 1 ? -18 : 0;
  const childOpacity = phase === 0 ? 0.9 : 1;
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        Substitution: f ∘<sub>2</sub> g
      </figcaption>
      <svg
        viewBox="0 0 220 200"
        role="img"
        aria-label="Operadic substitution: child operation slides into a parent's leaf"
      >
        <OperadicEdges accent={accent} />
        <Leaf spec={ARITY3_LEAF_1} accent={accent} safe />
        <Leaf spec={ARITY3_LEAF_3} accent={accent} safe />
        <HubNode cx={ARITY3_HUB.x} cy={ARITY3_HUB.y} label="f" accent={accent} />
        <RootNode
          cx={ARITY3_ROOT.x}
          cy={ARITY3_ROOT.y}
          label={showChildAttached ? "f ∘₂ g (safe)" : "f(x₁, ?, x₃)"}
          accent={accent}
          safe={showChildAttached}
        />
        {!showChildAttached && (
          <circle
            cx={ARITY3_LEAF_2.x}
            cy={ARITY3_LEAF_2.y}
            r={11}
            fill="white"
            stroke={GRAY[400]}
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
        )}
        <g
          style={{
            transition: "transform 700ms ease, opacity 320ms ease",
            transform: `translateY(${childTranslateY}px)`,
            opacity: childOpacity,
          }}
        >
          <line x1={74} y1={28} x2={96} y2={50} stroke={accent} strokeWidth={1.5} />
          <line x1={118} y1={28} x2={96} y2={50} stroke={accent} strokeWidth={1.5} />
          <Leaf spec={{ id: "g-1", x: 74, y: 22, label: "y₁" }} accent={accent} safe />
          <Leaf spec={{ id: "g-2", x: 118, y: 22, label: "y₂" }} accent={accent} safe />
          <HubNode cx={96} cy={56} label="g" accent={accent} safe={showChildAttached} />
        </g>
        <text x={6} y={188} fontSize="10" fontFamily={FONT.body} fill={GRAY[600]}>
          {phase < 2
            ? "Slot 2 of f is open; g (arity 2) is the candidate substitution."
            : "g is plugged into slot 2; the composite f ∘₂ g has arity 4."}
        </text>
      </svg>
    </figure>
  );
}

/**
 * Closure illustration: every leaf is green-safe, every internal node
 * inherits green-safe, the root inherits green-safe. The animation flows
 * the safety color upward from leaves to root.
 *
 * This is the picture of the COMPOSE neutralization theorem.
 */
export function SafetyClosureTree({ accent }: { readonly accent: string }): React.JSX.Element {
  const phase = usePulsePhase(3, ANIMATION_PERIOD_MS);
  const showHub = phase >= 1;
  const showRoot = phase >= 2;
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        Closure: safe-in → safe-out
      </figcaption>
      <svg
        viewBox="0 0 192 168"
        role="img"
        aria-label="Safety closure: green leaves propagate to a green root"
      >
        <OperadicEdges accent={accent} />
        {ARITY3_LEAVES.map((leaf) => (
          <Leaf key={leaf.id} spec={leaf} accent={accent} safe />
        ))}
        <g style={{ transition: "opacity 320ms ease", opacity: showHub ? 1 : 0.25 }}>
          <HubNode cx={ARITY3_HUB.x} cy={ARITY3_HUB.y} label="f" accent={accent} safe />
        </g>
        <g style={{ transition: "opacity 320ms ease", opacity: showRoot ? 1 : 0.25 }}>
          <RootNode
            cx={ARITY3_ROOT.x}
            cy={ARITY3_ROOT.y}
            label="safe composite"
            accent={accent}
            safe
          />
        </g>
        <g style={{ transition: "opacity 320ms ease", opacity: showRoot ? 1 : 0 }}>
          <path d="M30 158 L162 158" stroke={SAFE[700]} strokeWidth={2.5} />
          <text
            x={96}
            y={154}
            textAnchor="middle"
            fontSize="10"
            fontFamily={FONT.mono}
            fill={SAFE[700]}
          >
            P(n) Safe → Safe
          </text>
        </g>
      </svg>
    </figure>
  );
}

/**
 * Programs as trees in a free operad. Leaves carry input types; internal
 * nodes carry canonical CSK generator names. The whole tree is the program;
 * safety is a property of leaves that the operad pushes to the root by
 * construction.
 *
 * Node labels use canonical, non-legacy names: `verify` and `seal` are
 * descriptive verbs (no legacy aliases like `auth` per AGENTS.md §
 * Non-Negotiable rule 6).
 */
export function FreeOperadProgramTree({ accent }: { readonly accent: string }): React.JSX.Element {
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        Free operad: a program as a typed tree
      </figcaption>
      <svg
        viewBox="0 0 260 180"
        role="img"
        aria-label="Free-operad term: typed leaves and named generator nodes form a program tree"
      >
        <g stroke={accent} strokeWidth={1.5} fill="none">
          <line x1={130} y1={146} x2={70} y2={104} />
          <line x1={130} y1={146} x2={200} y2={104} />
          <line x1={70} y1={92} x2={30} y2={48} />
          <line x1={70} y1={92} x2={110} y2={48} />
          <line x1={200} y1={92} x2={170} y2={48} />
          <line x1={200} y1={92} x2={230} y2={48} />
        </g>
        <Leaf spec={{ id: "free-1", x: 30, y: 38, label: "req" }} accent={accent} safe />
        <Leaf spec={{ id: "free-2", x: 110, y: 38, label: "ctx" }} accent={accent} safe />
        <Leaf spec={{ id: "free-3", x: 170, y: 38, label: "evd" }} accent={accent} safe />
        <Leaf spec={{ id: "free-4", x: 230, y: 38, label: "key" }} accent={accent} safe />
        <HubNode cx={70} cy={100} label="verify" accent={accent} safe />
        <HubNode cx={200} cy={100} label="seal" accent={accent} safe />
        <RootNode cx={130} cy={152} label="program (safe-by-construction)" accent={accent} safe />
        <text x={4} y={172} fontSize="9" fontFamily={FONT.mono} fill={GRAY[600]}>
          leaves: typed inputs · nodes: named safe generators · tree: a closed program term
        </text>
      </svg>
    </figure>
  );
}
